import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChefHat, Bike, Package, Home as HomeIcon, Star, X, LogIn, Receipt, Clock } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const STAGES = [
  { key: 'placed', label: 'Order Placed', icon: Receipt, animate: 'wiggle' },
  { key: 'preparing', label: 'Being Packed', icon: Package, animate: 'bounce' },
  { key: 'preparing', label: 'Cooking', icon: ChefHat, animate: 'cook' },
  { key: 'out_for_delivery', label: 'On the way', icon: Bike, animate: 'ride' },
  { key: 'delivered', label: 'Delivered', icon: HomeIcon, animate: 'pop' },
];

// Map order status → visual stage index (0..4)
const STATUS_INDEX = {
  placed: 0,
  preparing: 2, // show up to Cooking
  out_for_delivery: 3,
  delivered: 4,
  cancelled: -1,
};

const STAGE_ANIMATIONS = {
  wiggle: { rotate: [0, -8, 8, -6, 6, 0] },
  bounce: { y: [0, -6, 0, -3, 0] },
  cook: { rotate: [0, -12, 12, -6, 6, 0], scale: [1, 1.08, 1] },
  ride: { x: [0, 8, -4, 12, 0] },
  pop: { scale: [1, 1.15, 0.95, 1.05, 1] },
};

function AnimatedTracker({ statusKey }) {
  const currentIdx = STATUS_INDEX[statusKey] ?? 0;
  const cancelled = statusKey === 'cancelled';

  return (
    <div className="relative">
      {/* horizontal progress line (desktop) / vertical (mobile) */}
      <div className="hidden md:block absolute top-8 left-8 right-8 h-1 bg-borderbeige rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${(Math.max(currentIdx, 0) / (STAGES.length - 1)) * 100}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className={`h-full ${cancelled ? 'bg-red-400' : 'bg-gradient-to-r from-saffron to-fresh'} rounded-full`}
        />
      </div>
      <div className="grid grid-cols-5 gap-2 md:gap-3 relative">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const done = !cancelled && i <= currentIdx;
          const active = !cancelled && i === currentIdx;
          return (
            <div key={i} className="flex flex-col items-center text-center" data-testid={`tracker-stage-${i}`}>
              <motion.div
                animate={active ? STAGE_ANIMATIONS[s.animate] : {}}
                transition={active ? { duration: 1.6, repeat: Infinity, repeatDelay: 0.4 } : {}}
                className={`relative w-16 h-16 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-warm z-10 ${
                  done ? 'bg-gradient-primary text-white' : 'bg-white border-2 border-borderbeige text-textlight'
                }`}
              >
                <Icon className="w-7 h-7" />
                {done && !active && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-fresh flex items-center justify-center border-2 border-white"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                  </motion.div>
                )}
                {active && (
                  <motion.div
                    className="absolute inset-0 rounded-full bg-saffron/40"
                    animate={{ scale: [1, 1.35, 1], opacity: [0.7, 0, 0.7] }}
                    transition={{ duration: 1.8, repeat: Infinity }}
                  />
                )}
              </motion.div>
              <span className={`text-[10px] md:text-xs font-heading font-semibold mt-2 leading-tight ${
                done ? 'text-maroon' : 'text-textlight'
              }`}>{s.label}</span>
            </div>
          );
        })}
      </div>
      {cancelled && (
        <div className="mt-4 p-3 bg-red-50 rounded-xl text-center text-red-700 font-heading font-semibold">Order cancelled</div>
      )}
    </div>
  );
}

function ReviewModal({ order, onClose, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [hover, setHover] = useState(0);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/review`, { rating, text: text.trim() }, { withCredentials: true });
      toast.success('Thanks for your review!');
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="review-modal">
        <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-md p-5">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="font-heading font-bold text-lg text-textprimary">How was your meal?</h3>
              <p className="text-xs text-textsecondary font-body">Order #{order.id.slice(0, 8).toUpperCase()}</p>
            </div>
            <button onClick={onClose} data-testid="review-close-btn"><X className="w-5 h-5 text-textsecondary" /></button>
          </div>

          <div className="flex justify-center gap-2 my-6">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                data-testid={`review-star-${n}`}
                className="focus:outline-none"
              >
                <Star className={`w-9 h-9 transition-all ${n <= (hover || rating) ? 'fill-golden text-golden' : 'text-borderbeige'}`} />
              </button>
            ))}
          </div>

          <textarea
            data-testid="review-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="Tell us what you loved (optional)"
            className="w-full px-3 py-2 border border-borderbeige rounded-xl font-body text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40 resize-none"
          />

          <button
            onClick={submit}
            disabled={saving}
            data-testid="review-submit-btn"
            className="w-full mt-4 bg-saffron text-white py-3 rounded-2xl font-heading font-bold shadow-warm disabled:opacity-60"
          >
            {saving ? 'Sending…' : 'Submit Review'}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewData, setReviewData] = useState({ reviewed: false });
  const [retrying, setRetrying] = useState(false);

  const handleRetryPayment = async () => {
    if (!order) return;
    setRetrying(true);
    try {
      const { data: cf } = await api.post('/payments/cashfree/checkout', {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      const cfInstance = window.Cashfree ? window.Cashfree({ mode: cf.environment }) : null;
      if (cfInstance && cf.payment_session_id) {
        cfInstance.checkout({
          paymentSessionId: cf.payment_session_id,
          redirectTarget: "_self"
        });
      } else {
        window.location.href = cf.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to initialize payment');
      setRetrying(false);
    }
  };

  useEffect(() => {
    const fetch = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
    fetch();
    const interval = setInterval(fetch, 5000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    if (id) api.get(`/orders/${id}/review`).then((r) => setReviewData(r.data)).catch(() => {});
  }, [id]);

  if (!order) return <div className="p-8 text-center text-textsecondary">Loading...</div>;

  const isDelivered = order.status === 'delivered';

  return (
    <div className="pb-24 md:pb-8 max-w-3xl mx-auto px-4 md:px-6 py-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`rounded-3xl p-6 mb-6 shadow-warm text-white bg-gradient-to-br ${
          order.status === 'payment_pending'
            ? 'from-amber-500 to-orange-600'
            : 'from-fresh to-emerald-600'
        }`}
      >
        <div className="flex items-center gap-3 mb-2">
          {order.status === 'payment_pending' ? <Clock className="w-8 h-8 animate-pulse" /> : <CheckCircle2 className="w-8 h-8" />}
          <h1 className="font-heading font-bold text-2xl">
            {order.status === 'payment_pending' ? 'Payment Pending' : isDelivered ? 'Order Delivered' : 'Order Confirmed'}
          </h1>
        </div>
        <p className="font-body text-white/90 text-sm">Order #{order.id.slice(0, 8).toUpperCase()}</p>
        <p className="font-body text-white/85 text-sm mt-2">
          {order.status === 'payment_pending'
            ? `Please complete payment to place your order, ${order.customer_name}.`
            : isDelivered
            ? `Hope you enjoyed the meal, ${order.customer_name}!`
            : `Thanks ${order.customer_name}, we're preparing your food with love.`}
        </p>
      </motion.div>

      {order.status === 'payment_pending' && (
        <section className="bg-white rounded-2xl p-5 shadow-card border border-amber-300 bg-amber-50/20 mb-5 text-center">
          <Clock className="w-12 h-12 text-amber-500 mx-auto mb-3 animate-pulse" />
          <h2 className="font-heading font-bold text-lg text-textprimary mb-1">Retry Order Payment</h2>
          <p className="text-sm text-textsecondary font-body mb-4">
            If your online payment failed or was closed, you can retry it now to place the order.
          </p>
          <button
            onClick={handleRetryPayment}
            disabled={retrying}
            className="bg-saffron hover:bg-saffron-dark text-white px-6 py-3 rounded-2xl font-heading font-bold shadow-warm disabled:opacity-60 flex items-center gap-2 mx-auto transition-colors"
          >
            {retrying ? 'Connecting to payment gateway...' : 'Pay Now / Retry Payment'}
          </button>
        </section>
      )}

      {order.status !== 'payment_pending' && (
        <section className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige mb-5">
          <h2 className="font-heading font-bold text-lg text-textprimary mb-6">Order Status</h2>
          <AnimatedTracker statusKey={order.status} />
        </section>
      )}

      {order.scheduled_time && (
        <section className="bg-amber-50 rounded-2xl p-4 border border-amber-200 mb-5 flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-700 shrink-0" />
          <div className="text-sm font-body text-amber-800">
            This is a <b>Pre-order</b> scheduled for delivery today in the slot: <b>{new Date(order.scheduled_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</b>
          </div>
        </section>
      )}

      {isDelivered && !reviewData.reviewed && (
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-offer p-5 rounded-2xl mb-5 shadow-warm"
          data-testid="review-prompt"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-heading font-bold text-maroon">Loved your meal?</h3>
              <p className="text-xs text-maroon/80 font-body">Leave a quick review to help others discover Pushpanjali.</p>
            </div>
            <button
              onClick={() => setReviewOpen(true)}
              data-testid="open-review-btn"
              className="bg-maroon text-white px-4 py-2.5 rounded-2xl font-heading font-semibold text-sm shadow-warm shrink-0"
            >
              Rate Order
            </button>
          </div>
        </motion.section>
      )}

      {isDelivered && reviewData.reviewed && (
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-fresh/10 p-4 rounded-2xl mb-5 flex items-center gap-3" data-testid="review-submitted-banner">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`w-4 h-4 ${n <= reviewData.rating ? 'fill-golden text-golden' : 'text-borderbeige'}`} />
            ))}
          </div>
          <div className="text-sm font-body text-fresh"><b>Thanks for rating!</b> {reviewData.text && `“${reviewData.text}”`}</div>
        </motion.section>
      )}

      <section className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige mb-5">
        <h2 className="font-heading font-bold text-lg text-textprimary mb-3">Items</h2>
        <div className="space-y-3">
          {order.items.map((i) => (
            <div key={i.item_id} className="flex gap-3">
              <img src={i.image_url} alt={i.name} className="w-14 h-14 rounded-xl object-cover" />
              <div className="flex-1">
                <div className="font-heading font-semibold text-sm text-textprimary">{i.name}</div>
                <div className="text-xs text-textsecondary font-body">Qty {i.quantity} × ₹{i.price}</div>
              </div>
              <div className="font-heading font-bold text-maroon">₹{(i.price * i.quantity).toFixed(2)}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-borderbeige mt-4 pt-3 space-y-1 font-body text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>₹{order.subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Delivery</span><span>₹{order.delivery_fee.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Tax</span><span>₹{order.tax.toFixed(2)}</span></div>
          <div className="flex justify-between font-heading font-bold text-lg pt-2">
            <span>Total</span><span className="text-maroon">₹{order.total.toFixed(2)}</span>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige mb-5">
        <h2 className="font-heading font-bold text-lg text-textprimary mb-2">Delivery to</h2>
        <p className="font-body text-sm text-textprimary">{order.customer_name} • {order.customer_phone}</p>
        <p className="font-body text-sm text-textsecondary mt-1">{order.delivery_address}</p>
      </section>

      <div className="text-center">
        <Link to="/menu" className="text-maroon font-heading font-semibold">← Continue shopping</Link>
      </div>

      {reviewOpen && (
        <ReviewModal
          order={order}
          onClose={() => setReviewOpen(false)}
          onSubmitted={() => { setReviewOpen(false); setReviewData({ reviewed: true, rating: 5 }); api.get(`/orders/${id}/review`).then((r) => setReviewData(r.data)).catch(() => {}); }}
        />
      )}
    </div>
  );
}

export function OrdersList() {
  const { user, loginWithGoogle, authClient } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const { data } = await authClient.get('/orders');
        if (isMounted) setOrders(data);
      } catch (e) {
        console.error('Failed to load orders', e);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [user, authClient]);

  // Login gate for logged-out users
  if (!user) {
    return (
      <div className="pb-24 md:pb-8 max-w-md mx-auto px-4 py-16 text-center">
        <Receipt className="w-16 h-16 text-saffron mx-auto mb-4" />
        <h2 className="font-heading font-bold text-2xl text-textprimary mb-2">Sign in to view your orders</h2>
        <p className="text-textsecondary font-body mb-6">Track live orders, re-order favourites and leave reviews after delivery.</p>
        <button onClick={loginWithGoogle} data-testid="orders-signin-btn" className="inline-flex items-center gap-2 bg-saffron text-white px-6 py-3 rounded-2xl font-heading font-semibold shadow-warm">
          <LogIn className="w-4 h-4" /> Sign in with Google
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="p-8 text-center text-textsecondary font-body">Loading your orders...</div>;
  }

  return (
    <div className="pb-24 md:pb-8 max-w-3xl mx-auto px-4 md:px-6 py-6">
      <h1 className="font-heading font-extrabold text-3xl text-textprimary mb-6">My Orders</h1>
      {orders.length === 0 && (
        <div className="text-center text-textsecondary font-body py-10">No orders found for your account.</div>
      )}
      <div className="space-y-3">
        {orders.map((o) => (
          <Link to={`/orders/${o.id}`} key={o.id} data-testid={`order-item-${o.id}`} className="block bg-white rounded-2xl p-4 shadow-card border border-borderbeige hover:shadow-warm transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-heading font-bold text-textprimary">#{o.id.slice(0, 8).toUpperCase()}</div>
                <div className="text-xs text-textsecondary font-body mt-1">{o.items.length} items • ₹{o.total.toFixed(2)}</div>
                {o.scheduled_time && (
                  <div className="inline-flex items-center gap-1 mt-2 text-[10px] text-amber-800 font-heading font-semibold bg-amber-50 px-2 py-0.5 rounded">
                    <Clock className="w-3 h-3" /> Scheduled: {new Date(o.scheduled_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </div>
                )}
              </div>
              <span className={`text-xs font-heading font-semibold px-3 py-1 rounded-full ${
                o.status === 'delivered' ? 'bg-fresh/10 text-fresh' :
                o.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                o.status === 'payment_pending' ? 'bg-amber-100 text-amber-800' :
                'bg-golden/20 text-maroon'
              }`}>{o.status === 'payment_pending' ? 'Payment Pending' : o.status.replace(/_/g, ' ')}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
